import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, DollarSign, TrendingUp, 
  Target, BarChart3, PieChart, Calculator, Zap, Award,
  CheckCircle, Calendar, Users, Cloud, Shield,
  Lightbulb, Rocket, Sparkles, Brain, Globe, Activity
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart as RechartsPieChart, Cell, Pie, AreaChart, Area } from 'recharts';

interface BudgetItem {
  id: string;
  category: string;
  subcategory: string;
  current: number;
  previous: number;
  forecast: number;
  variance: number;
  roi: number;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'On Track' | 'Over Budget' | 'Under Budget' | 'At Risk';
  description: string;
  recommendations: string[];
  savings: number;
  investments: number;
}

interface BudgetScenario {
  id: string;
  name: string;
  description: string;
  totalBudget: number;
  savings: number;
  roi: number;
  timeline: string;
  risk: 'Low' | 'Medium' | 'High';
  items: BudgetItem[];
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface BudgetTIOptimiseProps {
  isOpen: boolean;
  onClose: () => void;
}

const budgetData: BudgetItem[] = [
  {
    id: 'infrastructure',
    category: 'Infrastructure',
    subcategory: 'Serveurs & Stockage',
    current: 450000,
    previous: 420000,
    forecast: 480000,
    variance: 7.1,
    roi: 185,
    priority: 'Critical',
    status: 'On Track',
    description: 'Infrastructure serveurs, stockage et virtualisation',
    recommendations: ['Migration cloud hybride', 'Optimisation licences', 'Consolidation serveurs'],
    savings: 75000,
    investments: 120000
  },
  {
    id: 'security',
    category: 'Sécurité',
    subcategory: 'Cybersécurité & Conformité',
    current: 320000,
    previous: 280000,
    forecast: 350000,
    variance: 14.3,
    roi: 240,
    priority: 'Critical',
    status: 'Over Budget',
    description: 'Solutions de sécurité, audits et conformité',
    recommendations: ['IA pour détection', 'Automatisation SOC', 'Formation équipes'],
    savings: 45000,
    investments: 80000
  },
  {
    id: 'software',
    category: 'Logiciels',
    subcategory: 'Licences & Applications',
    current: 280000,
    previous: 300000,
    forecast: 260000,
    variance: -6.7,
    roi: 160,
    priority: 'High',
    status: 'Under Budget',
    description: 'Licences logicielles et applications métier',
    recommendations: ['Audit licences', 'Négociation contrats', 'Alternatives open source'],
    savings: 60000,
    investments: 40000
  },
  {
    id: 'personnel',
    category: 'Personnel',
    subcategory: 'Salaires & Formation',
    current: 1200000,
    previous: 1150000,
    forecast: 1250000,
    variance: 4.3,
    roi: 220,
    priority: 'Critical',
    status: 'On Track',
    description: 'Salaires équipe TI et formation continue',
    recommendations: ['Formation IA', 'Certifications cloud', 'Programme mentorat'],
    savings: 0,
    investments: 150000
  },
  {
    id: 'cloud',
    category: 'Cloud & Services',
    subcategory: 'AWS, Azure, GCP',
    current: 180000,
    previous: 150000,
    forecast: 220000,
    variance: 20.0,
    roi: 195,
    priority: 'High',
    status: 'At Risk',
    description: 'Services cloud et solutions SaaS',
    recommendations: ['Optimisation coûts', 'Réservation instances', 'Monitoring usage'],
    savings: 35000,
    investments: 50000
  },
  {
    id: 'innovation',
    category: 'Innovation',
    subcategory: 'IA, R&D, POC',
    current: 120000,
    previous: 80000,
    forecast: 180000,
    variance: 50.0,
    roi: 320,
    priority: 'Medium',
    status: 'On Track',
    description: 'Projets d\'innovation et recherche & développement',
    recommendations: ['POC IA générative', 'Automatisation processus', 'Analytics avancés'],
    savings: 0,
    investments: 120000
  }
];

const scenarios: BudgetScenario[] = [
  {
    id: 'conservative',
    name: 'Scénario Conservateur',
    description: 'Optimisation minimale avec focus sur la stabilité',
    totalBudget: 2550000,
    savings: 215000,
    roi: 180,
    timeline: '12 mois',
    risk: 'Low',
    items: budgetData
  },
  {
    id: 'balanced',
    name: 'Scénario Équilibré',
    description: 'Équilibre entre optimisation et innovation',
    totalBudget: 2700000,
    savings: 350000,
    roi: 220,
    timeline: '18 mois',
    risk: 'Medium',
    items: budgetData
  },
  {
    id: 'aggressive',
    name: 'Scénario Agressif',
    description: 'Transformation digitale avec investissements majeurs',
    totalBudget: 3200000,
    savings: 500000,
    roi: 280,
    timeline: '24 mois',
    risk: 'High',
    items: budgetData
  }
];

const createSlides = (): Slide[] => [
  {
    id: 'overview',
    title: 'Budget TI - Vue d\'Ensemble',
    subtitle: 'Analyse financière et performance budgétaire',
    icon: DollarSign,
    color: '#2ECC71',
    content: (
      <div className="space-y-6">
        {/* Métriques Globales */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <DollarSign className="w-8 h-8" />
            Budget TI 2024 - Performance Globale
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">$2.55M</div>
              <div className="text-white/90">Budget Total</div>
              <div className="text-sm text-white/80">+4.2% vs 2023</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-300">$215K</div>
              <div className="text-white/90">Économies Réalisées</div>
              <div className="text-sm text-white/80">8.4% du budget</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-300">220%</div>
              <div className="text-white/90">ROI Moyen</div>
              <div className="text-sm text-white/80">+15% vs 2023</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-300">92%</div>
              <div className="text-white/90">Précision Budget</div>
              <div className="text-sm text-white/80">±3% variance</div>
            </div>
          </div>
        </div>

        {/* Répartition par Catégorie */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-green-600" />
              Répartition du Budget par Catégorie
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={[
                      { name: 'Personnel', value: 1200000, color: '#3498DB' },
                      { name: 'Infrastructure', value: 450000, color: '#E74C3C' },
                      { name: 'Sécurité', value: 320000, color: '#F39C12' },
                      { name: 'Logiciels', value: 280000, color: '#9B59B6' },
                      { name: 'Cloud', value: 180000, color: '#2ECC71' },
                      { name: 'Innovation', value: 120000, color: '#E67E22' }
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {[
                      { name: 'Personnel', value: 1200000, color: '#3498DB' },
                      { name: 'Infrastructure', value: 450000, color: '#E74C3C' },
                      { name: 'Sécurité', value: 320000, color: '#F39C12' },
                      { name: 'Logiciels', value: 280000, color: '#9B59B6' },
                      { name: 'Cloud', value: 180000, color: '#2ECC71' },
                      { name: 'Innovation', value: 120000, color: '#E67E22' }
                    ].map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => [`$${value.toLocaleString()}`, 'Budget']} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Évolution Budget vs Prévision
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={budgetData.map(item => ({
                  category: item.category,
                  current: item.current / 1000,
                  forecast: item.forecast / 1000,
                  variance: item.variance
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip formatter={(value: any) => [`$${value}K`, 'Budget']} />
                  <Bar dataKey="current" fill="#3498DB" name="Actuel" />
                  <Bar dataKey="forecast" fill="#E74C3C" name="Prévision" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Performance par Catégorie */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-green-600" />
            Performance Détaillée par Catégorie
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {budgetData.map((item) => (
              <div key={item.id} className="bg-white p-4 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-800">{item.category}</h4>
                    <p className="text-sm text-gray-600">{item.subcategory}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.status === 'On Track' ? 'bg-green-100 text-green-800' :
                    item.status === 'Over Budget' ? 'bg-red-100 text-red-800' :
                    item.status === 'Under Budget' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Budget:</span>
                    <span className="font-medium">${(item.current / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Variance:</span>
                    <span className={`font-medium ${item.variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {item.variance > 0 ? '+' : ''}{item.variance.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ROI:</span>
                    <span className="font-medium text-green-600">{item.roi}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'optimization',
    title: 'Optimisation & Économies',
    subtitle: 'Stratégies d\'optimisation et opportunités d\'économies',
    icon: TrendingUp,
    color: '#3498DB',
    content: (
      <div className="space-y-6">
        {/* Potentiel d'Économies */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <TrendingUp className="w-8 h-8" />
            Potentiel d'Optimisation Budget TI
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-300">$350K</div>
              <div className="text-white/90">Économies Potentielles</div>
              <div className="text-sm text-white/80">13.7% du budget</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-300">$500K</div>
              <div className="text-white/90">Investissements ROI</div>
              <div className="text-sm text-white/80">ROI 280%</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-yellow-300">18 mois</div>
              <div className="text-white/90">Période de Retour</div>
              <div className="text-sm text-white/80">Break-even</div>
            </div>
          </div>
        </div>

        {/* Stratégies d'Optimisation */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              Opportunités d'Économies
            </h3>
            <div className="space-y-4">
              {budgetData.filter(item => item.savings > 0).map((item) => (
                <div key={item.id} className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-gray-800">{item.category}</h4>
                    <span className="text-lg font-bold text-green-600">${(item.savings / 1000).toFixed(0)}K</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                  <div className="space-y-1">
                    {item.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        {rec}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Rocket className="w-5 h-5 text-purple-600" />
              Investissements Stratégiques
            </h3>
            <div className="space-y-4">
              {budgetData.filter(item => item.investments > 0).map((item) => (
                <div key={item.id} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-gray-800">{item.category}</h4>
                    <span className="text-lg font-bold text-purple-600">${(item.investments / 1000).toFixed(0)}K</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">ROI: {item.roi}%</p>
                  <div className="space-y-1">
                    {item.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                        <Lightbulb className="w-3 h-3 text-purple-500" />
                        {rec}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Analyse ROI */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-green-600" />
            Analyse ROI par Catégorie
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={budgetData.map(item => ({
                category: item.category,
                roi: item.roi,
                investment: item.investments / 1000,
                savings: item.savings / 1000
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="roi" fill="#2ECC71" name="ROI %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'scenarios',
    title: 'Scénarios Budgétaires',
    subtitle: 'Comparaison des scénarios et recommandations',
    icon: Calculator,
    color: '#9B59B6',
    content: (
      <div className="space-y-6">
        {/* Comparaison Scénarios */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Calculator className="w-8 h-8" />
            Scénarios Budgétaires 2024-2025
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {scenarios.map((scenario) => (
              <div key={scenario.id} className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                <h4 className="font-bold mb-2">{scenario.name}</h4>
                <p className="text-sm text-white/90 mb-3">{scenario.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Budget Total:</span>
                    <span className="font-bold">${(scenario.totalBudget / 1000000).toFixed(1)}M</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Économies:</span>
                    <span className="font-bold text-green-300">${(scenario.savings / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ROI:</span>
                    <span className="font-bold text-blue-300">{scenario.roi}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Timeline:</span>
                    <span className="font-bold">{scenario.timeline}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Risque:</span>
                    <span className={`font-bold ${
                      scenario.risk === 'Low' ? 'text-green-300' :
                      scenario.risk === 'Medium' ? 'text-yellow-300' :
                      'text-red-300'
                    }`}>
                      {scenario.risk}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommandation */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl border-l-4 border-green-500">
          <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
            <Award className="w-6 h-6 text-green-600" />
            Recommandation Stratégique
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Scénario Recommandé: Équilibré</h4>
              <p className="text-gray-700 mb-4">
                Le scénario équilibré offre le meilleur ratio risque/rendement avec des économies significatives 
                et des investissements stratégiques pour l'innovation.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Économies de $350K sur 18 mois
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  ROI de 220% avec risque modéré
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Investissements dans l'IA et l'innovation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Optimisation de l'infrastructure existante
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Actions Prioritaires</h4>
              <div className="space-y-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-semibold text-gray-800">Q1 2024</div>
                  <div className="text-sm text-gray-600">Audit licences et optimisation cloud</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-semibold text-gray-800">Q2 2024</div>
                  <div className="text-sm text-gray-600">Migration infrastructure et formation IA</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-semibold text-gray-800">Q3-Q4 2024</div>
                  <div className="text-sm text-gray-600">Déploiement solutions IA et monitoring</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline de Mise en Œuvre */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Timeline de Mise en Œuvre
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={[
                { month: 'Jan', budget: 200, savings: 0, roi: 0 },
                { month: 'Fév', budget: 210, savings: 15, roi: 50 },
                { month: 'Mar', budget: 220, savings: 35, roi: 100 },
                { month: 'Avr', budget: 230, savings: 60, roi: 150 },
                { month: 'Mai', budget: 240, savings: 90, roi: 180 },
                { month: 'Jun', budget: 250, savings: 125, roi: 200 },
                { month: 'Jul', budget: 260, savings: 165, roi: 220 },
                { month: 'Aoû', budget: 270, savings: 210, roi: 240 },
                { month: 'Sep', budget: 280, savings: 260, roi: 250 },
                { month: 'Oct', budget: 290, savings: 315, roi: 260 },
                { month: 'Nov', budget: 300, savings: 375, roi: 270 },
                { month: 'Déc', budget: 310, savings: 440, roi: 280 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="savings" stackId="1" stroke="#2ECC71" fill="#2ECC71" name="Économies (K$)" />
                <Area type="monotone" dataKey="roi" stackId="2" stroke="#3498DB" fill="#3498DB" name="ROI %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'insights',
    title: 'Insights & Prédictions',
    subtitle: 'Analyse prédictive et recommandations IA',
    icon: Brain,
    color: '#E74C3C',
    content: (
      <div className="space-y-6">
        {/* Insights IA */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Brain className="w-8 h-8" />
            Insights IA - Analyse Prédictive
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold mb-3">Prédictions 2024-2025</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  +15% augmentation budget cloud
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  +25% investissements cybersécurité
                </li>
                <li className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  +40% budget innovation IA
                </li>
                <li className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  +8% coûts personnel (inflation)
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Opportunités Identifiées</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Automatisation: -30% coûts opérationnels
                </li>
                <li className="flex items-center gap-2">
                  <Cloud className="w-4 h-4" />
                  Migration cloud: -20% infrastructure
                </li>
                <li className="flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  IA prédictive: +35% efficacité
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Optimisation globale: +25% ROI
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Recommandations IA */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              Recommandations IA Prioritaires
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">1. Optimisation Automatique des Coûts</h4>
                <p className="text-sm text-gray-600 mb-2">IA pour optimiser automatiquement les ressources cloud</p>
                <div className="text-sm text-blue-600 font-medium">Économies: $75K/an</div>
              </div>
              <div className="p-4 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">2. Prédiction des Besoins</h4>
                <p className="text-sm text-gray-600 mb-2">ML pour anticiper les besoins en ressources</p>
                <div className="text-sm text-green-600 font-medium">ROI: 180%</div>
              </div>
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">3. Analyse Prédictive Budget</h4>
                <p className="text-sm text-gray-600 mb-2">Prédiction des dépassements et optimisations</p>
                <div className="text-sm text-purple-600 font-medium">Précision: 95%</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-red-600" />
              KPIs de Performance
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
                <div>
                  <div className="font-semibold text-gray-800">Précision Budget</div>
                  <div className="text-sm text-gray-600">Variance vs prévision</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">92%</div>
                  <div className="text-xs text-gray-500">Objectif: 95%</div>
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                <div>
                  <div className="font-semibold text-gray-800">ROI Moyen</div>
                  <div className="text-sm text-gray-600">Retour sur investissement</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">220%</div>
                  <div className="text-xs text-gray-500">Objectif: 200%</div>
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                <div>
                  <div className="font-semibold text-gray-800">Économies Réalisées</div>
                  <div className="text-sm text-gray-600">vs budget initial</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-600">13.7%</div>
                  <div className="text-xs text-gray-500">Objectif: 10%</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-6 rounded-xl">
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Rocket className="w-6 h-6" />
            Prêt à Optimiser Votre Budget TI ?
          </h3>
          <p className="mb-4">
            Notre analyse IA révèle un potentiel d'économies de $350K avec un ROI de 220%. 
            L'optimisation de votre budget TI peut transformer votre organisation !
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">$350K</div>
              <div className="text-sm">Économies Potentielles</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">220%</div>
              <div className="text-sm">ROI Garanti</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg text-center">
              <div className="text-2xl font-bold">18 mois</div>
              <div className="text-sm">Période de Retour</div>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function BudgetTIOptimise({ isOpen, onClose }: BudgetTIOptimiseProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = createSlides();

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Main Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
